'use client';

import React, { useCallback, useRef, useState } from 'react';
import { ArrowRight, Globe, Sparkles } from 'lucide-react';
import AnimateOnScroll from './AnimateOnScroll';
import CustomerLogos from './CustomerLogos';
import GradientText from './GradientText';
import HeroGrid from './hero/HeroGrid';
import HeroContentMask from './hero/HeroContentMask';
import HeroDecorations from './hero/HeroDecorations';
import ShortenResult from './hero/ShortenResult';
import DomainPicker from './hero/DomainPicker';
import TurnstileChallenge from './forms/TurnstileChallenge';
import { useFormGuard } from './forms/useFormGuard';
import type { SharedLinkCreated } from '@/lib/shorten';

const Hero: React.FC<{ domains?: string[] }> = ({ domains = [] }) => {
  const [url, setUrl] = useState('');
  // null = no preference, which is the API default (a random spread across the
  // pool, so no single domain absorbs the whole pool's reputation risk).
  const [domain, setDomain] = useState<string | null>(null);
  const [isShortening, setIsShortening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SharedLinkCreated | null>(null);
  const [challenge, setChallenge] = useState(false);
  // Bumped on every needsChallenge (and on errors during a challenge) so the
  // Turnstile widget remounts — a solved explicit widget never fires twice.
  const [challengeNonce, setChallengeNonce] = useState(0);
  const pending = useRef<Record<string, unknown> | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { guardFields, HoneypotField } = useFormGuard();

  // Guard-ladder lifecycle mirrors ContactForm: park the payload on
  // needsChallenge, retry it with the Turnstile token from onToken.
  const send = useCallback(async (payload: Record<string, unknown>) => {
    setIsShortening(true);
    setError(null);
    try {
      const res = await fetch('/api/shorten', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (body.needsChallenge) {
        pending.current = payload;
        setChallenge(true);
        setChallengeNonce((n) => n + 1);
        return;
      }
      pending.current = null;
      setChallenge(false);
      if (!res.ok) throw new Error(body.error || 'Something went wrong. Please try again.');
      setResult(body as SharedLinkCreated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setChallenge(false);
      setChallengeNonce((n) => n + 1);
    } finally {
      setIsShortening(false);
    }
  }, []);

  const onToken = useCallback(
    (turnstileToken: string) => {
      if (pending.current) void send({ ...pending.current, turnstileToken });
    },
    [send],
  );

  const handleShorten = (e: React.FormEvent) => {
    e.preventDefault();
    if (isShortening || challenge) return;
    const trimmed = url.trim();
    if (!trimmed) return;
    setResult(null);
    void send({ url: trimmed, ...(domain ? { domain } : {}), ...guardFields() });
  };

  return (
    <div className="relative pb-20 lg:pb-32 overflow-hidden">
      {/* Background: page-grid var sync + the randomized, full-viewport decoration
          field (HeroDecorations scatters cubes/stars/panels/labels into the empty
          squares around the centered content and reshuffles them periodically). */}
      <div className="absolute inset-0 z-0">
        <HeroGrid />
        {/* Grid-snapped clearing behind the content — scoped to this hero's content
            (contentRef) and painted before the decorations so they render on top. */}
        <HeroContentMask contentRef={contentRef} />
        <HeroDecorations />
      </div>

      {/* Content laid out ON the grid: a CSS grid whose row height equals the live
          grid cell, started a whole number of cells down from the top so every row
          lines up with the page's background grid. Mobile: a natural, comfortably-
          spaced stack (the grid is too tight to honour on a phone). */}
      <div
        ref={contentRef}
        data-hero-content
        className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center gap-7 pt-28 md:gap-0 md:grid md:justify-items-center md:[grid-auto-rows:var(--grid-cell,72px)] md:[padding-top:calc(2*var(--grid-cell,72px))]"
      >
        {/* Row: badge */}
        <AnimateOnScroll animation="fade-up" delay={0} className="md:[grid-row:span_1] w-full flex items-center justify-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-100 border border-brand-200 text-brand-700 text-xs font-semibold">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500"></span>
            </span>
            Live on Premium: Smart Routing Rules
            <ArrowRight size={12} />
          </div>
        </AnimateOnScroll>

        {/* Row: headline (3 cells) */}
        <AnimateOnScroll animation="fade-up" delay={100} className="md:[grid-row:span_3] w-full flex items-center justify-center">
          <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
            Make every link a <br className="hidden md:block" />
            <span className="relative inline-block">
              {/* Glow anchored behind the gradient word (the reference hero's glow). */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[200%] w-[150%] -translate-x-1/2 -translate-y-1/2 animate-cube-pulse rounded-full blur-[70px] [background:radial-gradient(ellipse,rgba(250,67,127,0.5),transparent_70%)]"
              />
              <GradientText
                colors={['#800938', '#d70e62', '#fa437f']}
                animationSpeed={2}
                yoyo={false}
              >
                smart decision.
              </GradientText>
            </span>
          </h1>
        </AnimateOnScroll>

        {/* Row: subtitle */}
        <AnimateOnScroll animation="fade-up" delay={200} className="md:[grid-row:span_1] w-full flex items-center justify-center">
          <p className="max-w-2xl text-lg md:text-xl text-slate-600 leading-relaxed">
            Easy to use, and powerful enough to handle even your most complex routing configurations.
          </p>
        </AnimateOnScroll>

        {/* Empty row — grid shows through (md+ only; on mobile the gap handles spacing) */}
        <div aria-hidden className="hidden md:block md:[grid-row:span_1]" />

        {/* Row: interactive demo box (TWO cells tall, box vertically centered in
            them for more presence; result overlays below so it never knocks the
            grid rhythm out of alignment) */}
        {/* relative z-20 is load-bearing, and has to live HERE rather than on the
            result panel inside. AnimateOnScroll's `.is-visible` runs fadeInUp with
            `forwards`, so the final keyframe's `transform: translateY(0)` stays
            applied for good — and a transform creates a stacking context. Every
            AnimateOnScroll block is therefore a permanent stacking context at
            z-index auto, so among them only DOM order decides what paints on top.
            The social-proof rows come later, so they drew straight through the
            result panel; a z-index on the panel itself can never escape this
            wrapper's context. */}
        <AnimateOnScroll animation="fade-up" delay={300} className="relative z-20 md:[grid-row:span_2] w-full flex items-center justify-center">
          <div className="relative w-full max-w-4xl">
            {/* Brand gradient ring — color-highlights the hero's main actor
                without tinting the input itself; glows stronger on focus. */}
            <div className="rounded-[18px] bg-gradient-to-r from-brand-300 via-brand-500 to-brand-500 p-[1.5px] shadow-2xl shadow-brand-500/20 transition-shadow duration-300 focus-within:shadow-brand-500/35">
            <div className="h-full bg-white rounded-[16.5px] p-2.5">
              <form onSubmit={handleShorten} className="flex flex-col sm:flex-row gap-2">
                <HoneypotField />
                <div className="relative flex-grow group">
                  <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-400">
                    <Globe size={20} />
                  </div>
                  <input
                    type="text"
                    placeholder="https://example.com/very-long-marketing-url-variant-b"
                    className="w-full pl-12 pr-4 py-4 sm:py-5 bg-slate-50 border border-transparent rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-brand-500/20 transition-all font-mono text-[15px]"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </div>

                {/* Real control in the slot two decorative "option" icons used
                    to fill — they were <button>s with no handler, focusable and
                    announced to screen readers, that did nothing when pressed.
                    Reusing the space keeps the form to one row on desktop. */}
                <DomainPicker domains={domains} value={domain} onChange={setDomain} />

                <button
                  type="submit"
                  disabled={isShortening || challenge}
                  className="px-9 py-4 sm:py-5 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-brand-500/25 flex items-center justify-center gap-2 min-w-[170px] disabled:opacity-70"
                >
                  {isShortening ? (
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Sparkles size={18} />
                      <span>Shorten</span>
                    </>
                  )}
                </button>
              </form>
            </div>
            </div>

            {/* Result Area — shows the promotion panel on success (the short URL
                is revealed only inside ShortenResult), the Turnstile escalation,
                or an error line.

                z-20 is load-bearing. From md up this is absolutely positioned
                with no stacking context between it and the social-proof rows
                below, which are later siblings — so without an explicit z-index
                the painting order falls back to DOM order and the "Trusted by"
                label and company logos draw straight through the panel. It read
                as a half-finished animation rather than what it was.

                It flies out of the form rather than merely appearing: the panel
                is anchored to the form's bottom edge and slides down into place
                as it fades, so the two read as connected.

                From md up it overlays: the grid below has a spacer row reserved
                for exactly this, so opening the panel doesn't knock the rhythm
                out of alignment. Below md that spacer is hidden, so overlaying
                would drop the panel straight onto the social-proof row — it must
                stay in flow and push the page down instead. The height clamp is
                per-breakpoint for the same reason: stacked on a phone the panel
                is far taller than the desktop overlay, and a shared 480px clipped
                its last control off the bottom. */}
            <div className={`relative z-20 md:absolute left-0 right-0 md:top-full mt-2 origin-top overflow-hidden transition-[max-height,opacity,transform] duration-300 ease-out ${result || challenge || error ? 'max-h-[900px] opacity-100 translate-y-0 md:max-h-[520px]' : 'pointer-events-none max-h-0 -translate-y-2 opacity-0'}`}>
              {result ? (
                <ShortenResult key={result.id} result={result} />
              ) : challenge ? (
                <TurnstileChallenge key={challengeNonce} onToken={onToken} />
              ) : error ? (
                <p role="alert" className="bg-red-50 border border-red-200 text-red-800 text-sm font-medium rounded-xl px-4 py-3 text-left">
                  {error}
                </p>
              ) : null}
            </div>
          </div>
        </AnimateOnScroll>

        {/* Empty row — grid shows through (md+ only; on mobile the gap handles spacing) */}
        <div aria-hidden className="hidden md:block md:[grid-row:span_1]" />

        {/* Row: social-proof label */}
        <AnimateOnScroll animation="fade-up" delay={400} className="md:[grid-row:span_1] w-full flex items-center justify-center">
          <p className="text-sm font-semibold text-slate-400 uppercase tracking-widest">Running links for teams like</p>
        </AnimateOnScroll>

        {/* Row: company logos — one grid cell tall, vertically centred in it */}
        <AnimateOnScroll animation="fade-up" delay={500} className="md:[grid-row:span_1] w-full flex items-center justify-center">
          <div className="w-full lg:px-[var(--grid-cell,72px)]">
            <CustomerLogos />
          </div>
        </AnimateOnScroll>
      </div>
    </div>
  );
};

export default Hero;
